import {
  Banner,
  Bleed,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Scrollable,
  SkeletonThumbnail,
  Text,
} from '@shopify/polaris';
import { PlusIcon } from '@shopify/polaris-icons';
import { SelectableThumbnail } from './SelectableThumbnail';
import { memo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Image } from '@/types/media';
import { InputWrapper } from '@/features/video-generation-stepper/inputs/InputWrapper';

export type ScrollableThumbnailSelectProps<T extends Image> = {
  items: T[];
  title?: string | null;
  description?: string;
  selected: T | null;
  loading?: boolean;
  minHeight?: string;
  minWidth?: string;
  imageClassName?: string;
  footer?: React.ReactNode;
  moreButtonClassName?: string;
  imageContainerClassName?: string;
  onMoreClick?: () => void;
  onSelect: (item: T) => void;
};

const ScrollableThumbnailSelect = <T extends Image>({
  items,
  title = 'Select an image',
  description,
  footer,
  selected,
  minWidth = '80px',
  minHeight = '80px',
  loading = false,
  imageClassName,
  moreButtonClassName,
  imageContainerClassName,
  onSelect,
  onMoreClick,
}: ScrollableThumbnailSelectProps<T>) => {
  useEffect(() => {
    if (!selected && !loading && items.length) {
      onSelect(items[0]);
    }
  }, [items, loading, onSelect, selected]);

  const getContentJsx = () => {
    if (!items.length && !onMoreClick) {
      return (
        <Banner tone="warning">
          <p>No images found</p>
        </Banner>
      );
    }

    return (
      <InlineStack gap="400" blockAlign="stretch" wrap={false}>
        {items.map((item, index) => (
          <Box key={`${item.id}-${index}`} minHeight={minHeight} minWidth={minWidth}>
            <SelectableThumbnail
              image={item}
              imageClassName={imageClassName}
              imageContainerClassName={imageContainerClassName}
              selected={!!selected && item.id === selected.id}
              onClick={onSelect}
            />
          </Box>
        ))}
      </InlineStack>
    );
  };

  return (
    <InputWrapper title={title || ''} description={description}>
      <BlockStack gap="400">
        <Bleed marginInline="400" marginBlockEnd="200">
          <Scrollable vertical>
            <Box paddingInline="400" paddingBlockEnd="200">
              {getContentJsx()}
            </Box>
          </Scrollable>
        </Bleed>

        {!loading && onMoreClick && (
          <InlineStack gap="400" align="start">
            <Box>
              <Button fullWidth icon={PlusIcon} onClick={onMoreClick}>
                Select an image
              </Button>
            </Box>
          </InlineStack>
        )}
      </BlockStack>
      {footer}
    </InputWrapper>
  );
};

export default memo(ScrollableThumbnailSelect);
